import { Controller } from "@hotwired/stimulus"

// The calendar invite card (live/_calendar_invite_card): guests as chips with a
// contact typeahead, and the rest plain form fields submitted with Approve.
//
// - Enter in a field never submits: here, submitting sends the invite.
// - Any edit marks the card dirty, so the chat poll keeps this node (and what
//   was typed) through its re-renders - see assistant_poll_controller.
const EMAIL = /^[^@\s<>,;]+@[^@\s<>,;]+\.[^@\s<>,;]+$/
const SEARCH_DELAY_MS = 160

export default class extends Controller {
    static targets = ["guests", "input", "suggestions", "chipTemplate", "hint", "starts", "ends", "availability"]
    static values = { recipientsUrl: String, availabilityUrl: String }

    connect() {
        this._results = []
        this._active = -1
        this._onSubmitEnd = () => {
            // A refused edit (no title, ends before it starts) leaves the card
            // in place with its toast; let the user fix it and try again.
            setTimeout(() => this._setDisabled(false), 600)
        }
        this.element.addEventListener("turbo:submit-start", () => this._setDisabled(true))
        this.element.addEventListener("turbo:submit-end", this._onSubmitEnd)
        this._checkAvailability()
    }

    // --- free/busy ----------------------------------------------------------

    // Moving the start keeps the length: a 30-minute meeting stays 30 minutes.
    timeChanged(event) {
        if (event.target === this.startsTarget && this._lengthMs) {
            const start = new Date(this.startsTarget.value)
            if (!Number.isNaN(start.getTime())) this.endsTarget.value = this._local(new Date(start.getTime() + this._lengthMs))
        }
        this.dirty()
        clearTimeout(this._availabilityTimer)
        this._availabilityTimer = setTimeout(() => this._checkAvailability(), 350)
    }

    async _checkAvailability() {
        if (!this.hasAvailabilityTarget || !this.availabilityUrlValue) return
        const starts = this.startsTarget.value
        const ends = this.endsTarget.value
        const startMs = new Date(starts).getTime()
        const endMs = new Date(ends).getTime()
        if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs > startMs) this._lengthMs = endMs - startMs
        const ticket = (this._ticket = (this._ticket || 0) + 1)
        try {
            const url = `${this.availabilityUrlValue}?starts_at=${encodeURIComponent(starts)}&ends_at=${encodeURIComponent(ends)}`
            const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            const data = await response.json()
            if (ticket === this._ticket) this._showAvailability(data)
        } catch (_e) {
            if (ticket === this._ticket) this.availabilityTarget.hidden = true
        }
    }

    _showAvailability(data) {
        const box = this.availabilityTarget
        box.replaceChildren()
        box.classList.remove("event-card-availability--free", "event-card-availability--busy")
        if (data.state === "free") {
            box.classList.add("event-card-availability--free")
            box.append(this._icon("bi-check-circle"), " You're free then.")
        } else if (data.state === "busy") {
            box.classList.add("event-card-availability--busy")
            const names = (data.conflicts || []).map((c) => `${c.title} (${c.label})`).join(", ")
            box.append(this._icon("bi-exclamation-circle"), ` Overlaps ${names}.`)
            if (data.suggestion) {
                const move = document.createElement("button")
                move.type = "button"
                move.className = "event-card-move"
                move.textContent = `Move to ${data.suggestion.label}`
                move.addEventListener("click", () => {
                    this.startsTarget.value = data.suggestion.starts_at
                    this.endsTarget.value = data.suggestion.ends_at
                    this.dirty()
                    this._checkAvailability()
                })
                box.append(" ", move)
            }
        } else {
            box.hidden = true
            return
        }
        box.hidden = false
    }

    _icon(name) {
        const icon = document.createElement("i")
        icon.className = `bi ${name}`
        icon.setAttribute("aria-hidden", "true")
        return icon
    }

    _local(date) {
        const pad = (n) => String(n).padStart(2, "0")
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    dirty() {
        this.element.dataset.liveCardDirty = "true"
    }

    ignoreEnter(event) {
        const tag = event.target.tagName
        if (tag === "TEXTAREA" || tag === "BUTTON") return
        event.preventDefault()
    }

    focusInput(event) {
        if (event.target === event.currentTarget) this.inputTarget.focus()
    }

    remove(event) {
        event.preventDefault()
        event.target.closest(".event-card-chip")?.remove()
        this._changed()
        this.inputTarget.focus()
    }

    search() {
        const value = this.inputTarget.value
        if (/[,;]\s*$/.test(value)) {
            this.inputTarget.value = value.replace(/[,;]\s*$/, "")
            this._commitTyped()
            return
        }
        clearTimeout(this._timer)
        this._timer = setTimeout(() => this._search(value.trim()), SEARCH_DELAY_MS)
    }

    guestKeydown(event) {
        const open = !this.suggestionsTarget.hidden && this._results.length > 0
        if (event.key === "ArrowDown" && open) {
            event.preventDefault()
            this._highlight(Math.min(this._active + 1, this._results.length - 1))
        } else if (event.key === "ArrowUp" && open) {
            event.preventDefault()
            this._highlight(Math.max(this._active - 1, 0))
        } else if (event.key === "Enter" || (event.key === "Tab" && this.inputTarget.value.trim())) {
            event.preventDefault()
            if (open && this._active >= 0) this._add(this._results[this._active])
            else this._commitTyped()
        } else if (event.key === "Escape") {
            this._hide()
        } else if (event.key === "Backspace" && !this.inputTarget.value) {
            const chips = this.guestsTarget.querySelectorAll(".event-card-chip")
            chips[chips.length - 1]?.remove()
            this._changed()
        }
    }

    guestBlur() {
        setTimeout(() => {
            if (document.activeElement === this.inputTarget) return
            this._commitTyped({ quiet: true })
            this._hide()
        }, 180)
    }

    async _search(query) {
        if (query.length < 2) return this._hide()
        try {
            const response = await fetch(`${this.recipientsUrlValue}?q=${encodeURIComponent(query)}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            const data = await response.json()
            if (this.inputTarget.value.trim() !== query) return
            const taken = new Set(this._emails())
            this._results = (data.recipients || []).filter((r) => !taken.has(r.email))
            this._render()
        } catch (_e) {
            this._hide()
        }
    }

    _render() {
        const box = this.suggestionsTarget
        box.replaceChildren()
        if (this._results.length === 0) return this._hide()
        this._results.forEach((guest, index) => {
            const row = document.createElement("button")
            row.type = "button"
            row.className = "outgoing-suggestion"
            row.setAttribute("role", "option")
            row.addEventListener("mousedown", (event) => {
                event.preventDefault()
                this._add(guest)
            })
            row.addEventListener("mouseenter", () => this._highlight(index))
            const name = document.createElement("span")
            name.className = "outgoing-suggestion-name"
            name.textContent = guest.name || guest.email
            const meta = document.createElement("span")
            meta.className = "outgoing-suggestion-meta"
            meta.textContent = guest.name && guest.name !== guest.email ? guest.email : (guest.meta || "")
            const text = document.createElement("span")
            text.className = "outgoing-suggestion-text"
            text.append(name, meta)
            row.append(text)
            box.append(row)
        })
        box.hidden = false
        this._highlight(0)
    }

    _highlight(index) {
        this._active = index
        Array.from(this.suggestionsTarget.children).forEach((row, i) => row.classList.toggle("is-active", i === index))
    }

    _hide() {
        this.suggestionsTarget.hidden = true
        this.suggestionsTarget.replaceChildren()
        this._results = []
        this._active = -1
    }

    _commitTyped({ quiet = false } = {}) {
        const value = this.inputTarget.value.trim().toLowerCase()
        if (!value) return
        if (EMAIL.test(value)) {
            this._add({ email: value })
        } else if (!quiet) {
            this.hintTarget.textContent = `"${value}" isn't an email address - pick someone from the list or type their address.`
        }
    }

    _add(guest) {
        if (!guest?.email || this._emails().includes(guest.email)) return
        const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true)
        const name = guest.name || guest.email
        chip.dataset.email = guest.email
        chip.title = guest.email
        chip.querySelector(".outgoing-chip-name").textContent = name
        chip.querySelector(".outgoing-chip-initial").textContent = name.trim().charAt(0).toUpperCase()
        chip.querySelector("input[type=hidden]").value = guest.email
        chip.querySelector(".outgoing-chip-remove").setAttribute("aria-label", `Remove ${name}`)
        if (guest.avatar_url) {
            const img = document.createElement("img")
            img.src = guest.avatar_url
            img.alt = ""
            img.addEventListener("error", () => img.remove(), { once: true })
            chip.querySelector(".outgoing-chip-face").append(img)
        }
        this.guestsTarget.insertBefore(chip, this.inputTarget)
        this.inputTarget.value = ""
        this._hide()
        this._changed()
    }

    _emails() {
        return Array.from(this.guestsTarget.querySelectorAll(".event-card-chip")).map((chip) => chip.dataset.email)
    }

    // The primary button says what will happen: an invite, or just the event.
    _changed() {
        this.dirty()
        const any = this._emails().length > 0
        const send = this.element.querySelector(".outgoing-send")
        if (send) send.textContent = any ? "Send invite" : "Add to calendar"
        this.hintTarget.textContent = ""
    }

    _setDisabled(disabled) {
        this.element.querySelectorAll(".event-card-footer button").forEach((button) => { button.disabled = disabled })
    }
}
